/* eslint-disable react/jsx-child-element-spacing, react/jsx-max-props-per-line, react/jsx-one-expression-per-line */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useState } from 'react';
import { useListReviewFlagsQuery, useResolveReviewFlagMutation } from './pipeline.endpoints';
import type { ReviewFlagDTO, ReviewFlagSeverity } from './types/pipeline.types';
import { BillingPipelineShell } from './billing-pipeline-shell';
import { ReviewFlagOpenButton } from './components/review-flag-open-button';

const SEVERITIES: ReviewFlagSeverity[] = ['low', 'medium', 'high', 'critical'];

export default function PipelineReviewPage() {
  useSetPageTitle('Billing — Review queue');
  const [searchParams, setSearchParams] = useSearchParams();
  const severity = (searchParams.get('severity') as ReviewFlagSeverity | null) || undefined;
  const showResolved = searchParams.get('resolved') === 'true';

  const setSeverity = useCallback(
    (s: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (!s) {
            p.delete('severity');
          } else {
            p.set('severity', s);
          }
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { data, isLoading, isError } = useListReviewFlagsQuery({
    resolved: showResolved ? true : false,
    severity,
  });

  const [resolveOpen, setResolveOpen] = useState(false);
  const [activeFlag, setActiveFlag] = useState<ReviewFlagDTO | null>(null);
  const [note, setNote] = useState('');
  const [resolveReviewFlag, { isLoading: isResolving }] = useResolveReviewFlagMutation();

  const rows = data?.results ?? [];

  const onResolveSubmit = async() => {
    if (!activeFlag) {
      return;
    }
    try {
      await resolveReviewFlag({
        id: activeFlag.id,
        body: { resolved: true, ...(note.trim() ? { note: note.trim() } : {}) },
      }).unwrap();
      setResolveOpen(false);
      setActiveFlag(null);
      setNote('');
    } catch {
      /* handled globally */
    }
  };

  return (
    <BillingPipelineShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-gray-600">
            Severity
            <select
              className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
              value={severity ?? ''}
              onChange={(e) => setSeverity(e.target.value)}
            >
              <option value="">All</option>
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => {
                setSearchParams(
                  (prev) => {
                    const p = new URLSearchParams(prev);
                    if (e.target.checked) {
                      p.set('resolved', 'true');
                    } else {
                      p.delete('resolved');
                    }
                    return p;
                  },
                  { replace: true }
                );
              }}
            />
            Show resolved
          </label>
        </div>

        <div className="rounded-md border border-gray-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-red-600">
                    Failed to load review flags.
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No flags in this queue.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell className="font-mono text-xs">{flag.id}</TableCell>
                    <TableCell>{flag.severity}</TableCell>
                    <TableCell className="max-w-[280px] truncate" title={flag.reason}>
                      {flag.reason}
                    </TableCell>
                    <TableCell className="text-xs">
                      {flag.content_type.model} ·{' '}
                      <span className="font-mono break-all">{flag.object_id}</span>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{flag.created_at}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <ReviewFlagOpenButton flag={flag} />
                      {!flag.resolved && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => {
                            setActiveFlag(flag);
                            setNote('');
                            setResolveOpen(true);
                          }}
                        >
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve flag #{activeFlag?.id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-gray-700 block">
              Note (optional)
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setResolveOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={isResolving} onClick={() => void onResolveSubmit()}>
              Mark resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BillingPipelineShell>
  );
}
